import React, { useState, useEffect } from 'react';
import {
    Package,
    IndianRupee,
    TrendingUp,
    AlertTriangle,
    Calendar,
    Activity,
    ShoppingCart,
    RotateCcw,
    Home,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import axiosInstance from '../api/axiosInstance';
import Authenticated from '../layout/Authenticated';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

const Dashboard = () => {
    const [dashboard, setDashboard] = useState(null);
    const [lowStock, setLowStock] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Date Filter States
    const [dateRangeType, setDateRangeType] = useState('today'); // today, yesterday, last7, custom
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [showCustomPicker, setShowCustomPicker] = useState(false);

    const fetchDashboard = async () => {
        setLoading(true);
        setError('');

        try {
            let startDate, endDate;

            const today = new Date();
            const todayStr = format(today, 'yyyy-MM-dd');

            if (dateRangeType === 'today') {
                startDate = todayStr;
                endDate = todayStr;
            } else if (dateRangeType === 'yesterday') {
                const yesterday = subDays(today, 1);
                const yesterdayStr = format(yesterday, 'yyyy-MM-dd');
                startDate = yesterdayStr;
                endDate = yesterdayStr;
            } else if (dateRangeType === 'last7') {
                const sevenDaysAgo = subDays(today, 6);
                startDate = format(sevenDaysAgo, 'yyyy-MM-dd');
                endDate = todayStr;
            } else if (dateRangeType === 'custom' && customStartDate && customEndDate) {
                startDate = customStartDate;
                endDate = customEndDate;
            } else {
                startDate = todayStr;
                endDate = todayStr;
            }

            const dashRes = await axiosInstance.get('/dashboard/stats', {
                params: { startDate, endDate }
            });

            setDashboard({
                ...dashRes.data?.data,
                dateRange: { start: startDate, end: endDate }
            });

            // Low Stock - Always current (not date filtered)
            try {
                const lowStockRes = await axiosInstance.get('/dashboard/low-stock');
                setLowStock(lowStockRes.data?.data || []);
            } catch (err) {
                console.warn("Low stock error:", err);
                setLowStock([]);
            }

        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboard();
    }, [dateRangeType, customStartDate, customEndDate]);

    const getDateRangeText = () => {
        if (!dashboard?.dateRange) return 'Loading...';

        const { start, end } = dashboard.dateRange;
        if (start === end) {
            if (dateRangeType === 'yesterday') return `Yesterday • ${format(new Date(start), 'dd MMM yyyy')}`;
            return format(new Date(start), 'dd MMM yyyy');
        }
        return `${format(new Date(start), 'dd MMM')} - ${format(new Date(end), 'dd MMM yyyy')}`;
    };

    if (loading) {
        return (
            <Authenticated>
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                    <div className="flex flex-col items-center">
                        <Activity className="w-10 h-10 text-teal-600 animate-spin" />
                        <p className="mt-3 text-gray-600 font-medium">Loading dashboard...</p>
                    </div>
                </div>
            </Authenticated>
        );
    }

    if (error) {
        return (
            <Authenticated>
                <div className="min-h-screen bg-gray-50 p-6">
                    <div className="max-w-7xl mx-auto">
                        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl">
                            <p className="font-medium">{error}</p>
                            <button
                                onClick={fetchDashboard}
                                className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </Authenticated>
        );
    }

    const { totalMedicines, sales, returns } = dashboard || {};

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
                <div className="max-w-full mx-auto space-y-8">

                    {/* Header with Date Filter */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center space-x-3">
                            <div className="bg-teal-600 p-3 rounded-xl shadow-md">
                                <Home className="h-8 w-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Pharmacy Dashboard</h1>
                                <p className="text-sm text-gray-600">Sales & Stock Overview</p>
                            </div>
                        </div>

                        {/* Date Filter */}
                        <div className="flex flex-col sm:flex-row items-end gap-3">
                            <div className="flex bg-white rounded-xl shadow-md p-1">
                                <button
                                    onClick={() => setDateRangeType('today')}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${dateRangeType === 'today' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                                >
                                    Today
                                </button>
                                <button
                                    onClick={() => setDateRangeType('yesterday')}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${dateRangeType === 'yesterday' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                                >
                                    Yesterday
                                </button>
                                <button
                                    onClick={() => setDateRangeType('last7')}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${dateRangeType === 'last7' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                                >
                                    Last 7 Days
                                </button>
                                <button
                                    onClick={() => setShowCustomPicker(!showCustomPicker)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-1 ${dateRangeType === 'custom' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                                >
                                    <Calendar className="w-4 h-4" />
                                    Custom
                                </button>
                            </div>

                            <div className="bg-white px-4 py-2 rounded-xl shadow-sm flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-teal-600" />
                                <span className="font-medium text-gray-700">{getDateRangeText()}</span>
                            </div>
                        </div>
                    </div>

                    {/* Custom Date Picker */}
                    {showCustomPicker && (
                        <div className="bg-white rounded-2xl shadow-lg p-5 border border-teal-200">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                                    <input
                                        type="date"
                                        value={customStartDate}
                                        onChange={(e) => {
                                            setCustomStartDate(e.target.value);
                                            setDateRangeType('custom');
                                        }}
                                        max={customEndDate || format(new Date(), 'yyyy-MM-dd')}
                                        className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                                    <input
                                        type="date"
                                        value={customEndDate}
                                        onChange={(e) => {
                                            setCustomEndDate(e.target.value);
                                            setDateRangeType('custom');
                                        }}
                                        min={customStartDate}
                                        max={format(new Date(), 'yyyy-MM-dd')}
                                        className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                                    />
                                </div>
                            </div>
                            <div className="mt-4 flex justify-end">
                                <button
                                    onClick={() => setShowCustomPicker(false)}
                                    className="px-5 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {/* Cards same as before */}
                        <div className="bg-blue-600 text-white p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-all duration-300">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-blue-100 text-sm font-medium">Total Medicines</p>
                                    <p className="text-3xl font-bold mt-1">{totalMedicines || 0}</p>
                                </div>
                                <Package className="w-10 h-10 text-blue-200" />
                            </div>
                        </div>

                        <div className="bg-emerald-600 text-white p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-all duration-300">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-green-100 text-sm font-medium">Sales Amount</p>
                                    <p className="text-3xl font-bold mt-1">₹{Number(sales?.totalAmount || 0).toFixed(2)}</p>
                                    <p className="text-xs mt-1 opacity-90">{sales?.count || 0} invoices</p>
                                </div>
                                <ShoppingCart className="w-10 h-10 text-green-200" />
                            </div>
                        </div>

                        <div className="bg-orange-600 text-white p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-all duration-300">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-orange-100 text-sm font-medium">Returns</p>
                                    <p className="text-3xl font-bold mt-1">₹{Number(returns?.totalAmount || 0).toFixed(2)}</p>
                                    <p className="text-xs mt-1 opacity-90">{returns?.count || 0} items</p>
                                </div>
                                <RotateCcw className="w-10 h-10 text-orange-200" />
                            </div>
                        </div>

                        <div className="bg-indigo-600 text-white p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-all duration-300">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-purple-100 text-sm font-medium">Low Stock</p>
                                    <p className="text-3xl font-bold mt-1">{lowStock.length}</p>
                                    <p className="text-xs mt-1 opacity-90">items below threshold</p>
                                </div>
                                <AlertTriangle className="w-10 h-10 text-purple-200" />
                            </div>
                        </div>
                    </div>

                    {/* Rest of the UI (Today's Activity & Low Stock) same as before */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Today's Activity */}
                        <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                            <h2 className="text-xl font-bold text-gray-800 mb-5 flex items-center gap-2">
                                <TrendingUp className="w-6 h-6 text-teal-600" />
                                Activity ({getDateRangeText()})
                            </h2>

                            {sales?.records?.length > 0 || returns?.records?.length > 0 ? (
                                <div className="space-y-4 max-h-96 overflow-y-auto">
                                    {sales.records?.map((sale) => (
                                        <div key={sale.id} className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                                            <div>
                                                <p className="font-medium text-gray-800">{sale.name || 'Walk-in'}</p>
                                                <p className="text-xs text-gray-600">Invoice #{sale.id}</p>
                                            </div>
                                            <p className="font-bold text-green-700">+₹{Number(sale.net_amount).toFixed(2)}</p>
                                        </div>
                                    ))}

                                    {returns.records?.map((ret, i) => (
                                        <div key={i} className="flex justify-between items-center p-3 bg-red-50 rounded-lg border border-red-200">
                                            <div>
                                                <p className="font-medium text-gray-800">{ret.customer_name}</p>
                                                <p className="text-xs text-gray-600">Reason: {ret.return_reason}</p>
                                            </div>
                                            <p className="font-bold text-red-700">-₹{Number(ret.total_return_amt).toFixed(2)}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-10 text-gray-500">
                                    <Activity className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                                    <p>No activity in selected date range</p>
                                </div>
                            )}
                        </div>

                        {/* Low Stock Section (same as before) */}
                        <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                            <div className="flex justify-between items-center mb-5">
                                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                                    Low Stock Medicines
                                </h2>
                                <p className="bg-red-500 rounded-xl text-white px-3 py-1 text-sm font-semibold">
                                    {lowStock.length} items
                                </p>
                            </div>

                            {lowStock.length > 0 ? (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                    {lowStock.map((item, i) => (
                                        <div key={i} className={`p-4 rounded-lg border-l-4 ${item.stock === 0 ? 'bg-red-50 border-red-500' : 'bg-orange-50 border-orange-500'}`}>
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <p className="font-semibold text-gray-800">{item.name}</p>
                                                    <p className="text-xs text-gray-600 mt-1">Reorder Level: {item.reorderLevel}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className={`font-bold text-lg ${item.stock === 0 ? 'text-red-600' : 'text-orange-600'}`}>
                                                        {item.stock} left
                                                    </p>
                                                    {item.stock === 0 && <p className="text-xs text-red-600 font-medium">OUT OF STOCK</p>}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-10 text-gray-500">
                                    <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                                    <p>All medicines in stock</p>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Top Selling Medicines Section */}
                    <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                        <h2 className="text-xl font-bold text-gray-800 mb-5 flex items-center gap-2">
                            <TrendingUp className="w-6 h-6 text-purple-600" />
                            Top Selling Medicines ({getDateRangeText()})
                        </h2>

                        {dashboard?.topSelling?.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="text-gray-500 text-sm border-b border-gray-200">
                                            <th className="py-2 pl-2">Medicine Name</th>
                                            <th className="py-2">Quantity Sold</th>
                                            <th className="py-2 pr-2 text-right">Total Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm text-gray-700">
                                        {dashboard.topSelling.map((item, i) => (
                                            <tr key={i} className="border-b border-gray-50 hover:bg-purple-50 transition">
                                                <td className="py-3 pl-2 font-medium">{item.name}</td>
                                                <td className="py-3">{item.quantity}</td>
                                                <td className="py-3 pr-2 text-right font-semibold text-purple-700">
                                                    ₹{Number(item.amount).toFixed(2)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-10 text-gray-500">
                                <Activity className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                                <p>No top selling data available</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Authenticated>
    );
};

export default Dashboard;